import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Alert, Table, Badge } from 'react-bootstrap';
import Sidebar from '../components/Sidebar';
import { apiGet } from '../utils/apiConfig';
import 'bootstrap-icons/font/bootstrap-icons.css';

function DashboardPage() {
  const [metricas, setMetricas] = useState({
    totalDocumentos: 0,
    documentosMedicos: 0,
    documentosAdministrativos: 0,
    documentosPendientes: 0,
    documentosAnulados: 0,
    documentosFinalizados: 0,
    documentosRechazados: 0,
    usuariosActivos: 0
  });
  const [ultimosDocumentos, setUltimosDocumentos] = useState([]);
  const [sidebarAbierto, setSidebarAbierto] = useState(true);
  const [sinPermisos, setSinPermisos] = useState(false);
  const [cargando, setCargando] = useState(true);
  
  // ⭐ FUNCIÓN PARA DECODIFICAR NOMBRES CON TILDES ⭐
  const decodificarNombre = (nombre) => {
    if (!nombre) return 'Usuario';
    try {
      return decodeURIComponent(nombre);
    } catch (error) {
      console.warn('Error decodificando nombre:', nombre);
      return nombre;
    }
  };

  // ⭐ OBTENER Y DECODIFICAR DATOS DEL USUARIO ⭐
  const nombresRaw = localStorage.getItem('nombres') || 'Usuario';
  const nombres = decodificarNombre(nombresRaw);
  const rol = localStorage.getItem('rol') || '';

  // 🔐 FUNCIONES DE VERIFICACIÓN DE PERMISOS
  const obtenerPermisos = () => {
    const permisos = localStorage.getItem('permisos');
    return permisos ? JSON.parse(permisos) : {};
  };

  const tienePermiso = (permiso) => {
    // ⭐ SUPERADMIN SIEMPRE TIENE TODOS LOS PERMISOS
    if (rol === 'superadmin') return true;
    const permisos = obtenerPermisos();
    return permisos[permiso] === true;
  };

  const puedeVerDocumentos = () => {
    // ⭐ SUPERADMIN SIEMPRE PUEDE VER
    if (rol === 'superadmin') return true;
    return tienePermiso('verDocumentosMedicos') || tienePermiso('verDocumentosAdministrativos');
  };

  // 📊 CONFIGURACIÓN DE MÉTRICAS VISIBLES
  const obtenerMetricasVisibles = () => {
    const configuracion = [
      {
        key: 'totalDocumentos',
        titulo: 'Total Documentos',
        icono: '📄',
        color: '#1a1e4b',
        mostrar: puedeVerDocumentos()
      },
      {
        key: 'documentosMedicos', 
        titulo: 'Documentos Médicos',
        icono: '🏥',
        color: 'primary',
        mostrar: tienePermiso('verDocumentosMedicos')
      },
      {
        key: 'documentosAdministrativos',
        titulo: 'Documentos Administrativos', 
        icono: '📋',
        color: 'warning',
        mostrar: tienePermiso('verDocumentosAdministrativos')
      },
      {
        key: 'usuariosActivos',
        titulo: 'Usuarios Activos',
        icono: '👥', 
        color: 'info',
        mostrar: tienePermiso('gestionarUsuarios')
      }
    ];

    return configuracion.filter(metrica => metrica.mostrar);
  };

  // ✅ VERIFICAR PERMISOS AL CARGAR
  useEffect(() => {
    // ⭐ SUPERADMIN SIEMPRE TIENE ACCESO
    if (rol === 'superadmin') {
      cargarDashboard();
      return;
    }

    const permisos = localStorage.getItem('permisos');
    if (permisos) {
      const permisosObj = JSON.parse(permisos);
      if (!permisosObj.verDocumentosMedicos && !permisosObj.verDocumentosAdministrativos) {
        setSinPermisos(true);
        setCargando(false);
        return;
      }
    } else {
      setSinPermisos(true);
      setCargando(false);
      return;
    }
    
    cargarDashboard();
  }, [rol]);

  const cargarDashboard = async () => {
    try {
      setCargando(true);
      
      const datosMetricas = await apiGet('/api/dashboard-metricas');
      setMetricas(datosMetricas);

      // ✅ FILTRAR ÚLTIMOS DOCUMENTOS SEGÚN PERMISOS
      let filtroTipo = '';
      
      // ⭐ SUPERADMIN VE TODOS
      if (rol !== 'superadmin') {
        if (tienePermiso('verDocumentosMedicos') && !tienePermiso('verDocumentosAdministrativos')) {
          filtroTipo = 'medico';
        } else if (tienePermiso('verDocumentosAdministrativos') && !tienePermiso('verDocumentosMedicos')) {
          filtroTipo = 'administrativo';
        }
      }

      const params = { 
        page: 1, 
        limit: 5,
        ...(filtroTipo && { tipo: filtroTipo })
      };
      
      const datosUltimos = await apiGet('/api/documentos-dashboard', { params });
      setUltimosDocumentos(datosUltimos.documentos || []);

    } catch (error) {
      console.error('Error cargando dashboard:', error);
    } finally {
      setCargando(false);
    }
  };

  const formatearFecha = (fecha) => {
    return new Date(fecha).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const obtenerBadgeEstado = (estado) => {
    switch (estado) {
      case 'pendiente':
        return <Badge bg="warning" text="dark">⌛ Pendiente</Badge>;
      case 'finalizado':
        return <Badge bg="success">✅ Finalizado</Badge>;
      case 'rechazado':
        return <Badge bg="danger">❌ Rechazado</Badge>;
      case 'anulado':
        return <Badge bg="secondary">⛔ Anulado</Badge>;
      default:
        return <Badge bg="secondary">❓ Desconocido</Badge>;
    }
  };

  const obtenerIconoTipo = (tipo) => {
    return tipo === 'medico' ? '🏥' : '📋';
  };

  // ✅ SI NO TIENE PERMISOS, MOSTRAR MENSAJE
  if (sinPermisos) {
    return (
      <div className="d-flex">
        <Sidebar onToggle={setSidebarAbierto} />
        <div
          className="flex-grow-1 d-flex align-items-center justify-content-center"
          style={{ 
            marginLeft: sidebarAbierto ? '250px' : '70px',
            transition: 'margin-left 0.3s',
            minHeight: '100vh',
            backgroundColor: '#f8f9fa'
          }}
        >
          <Alert variant="warning" className="text-center">
            <h4>🚫 Acceso Denegado</h4>
            <p>No tienes permisos para ver el dashboard.</p>
            <p>Necesitas al menos permisos para ver documentos médicos o administrativos.</p>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex">
      <Sidebar onToggle={setSidebarAbierto} />
      <div
        className="flex-grow-1"
        style={{ 
          marginLeft: sidebarAbierto ? '250px' : '70px',
          transition: 'margin-left 0.3s',
          padding: '20px 30px',
          minHeight: '100vh',
          backgroundColor: '#f8f9fa'
        }}
      >
        {/* HEADER */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="mb-1">📊 Dashboard Ejecutivo</h2>
            <p className="text-muted mb-0">Panel de control y métricas generales</p>
          </div>
          <div className="text-end">
            <div className="fw-bold" style={{ fontSize: '1.1rem', color: '#1a1e4b' }}>
              👋 Bienvenido, {nombres}
            </div>
            <small className="text-muted text-capitalize">
              {rol === 'superadmin' ? '👑 Super Admin' : rol.replace('admin', ' Admin')}
            </small>
          </div>
        </div>

        {cargando ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
            <p className="mt-3 text-muted">Cargando métricas...</p>
          </div>
        ) : (
          <>
            {/* MÉTRICAS PRINCIPALES */}
            <Row className="mb-4">
              {obtenerMetricasVisibles().map((metrica) => (
                <Col md={3} className="mb-3" key={metrica.key}>
                  <Card className="h-100 border-0 shadow-sm">
                    <Card.Body className="text-center">
                      <div style={{ fontSize: '2.5rem', marginBottom: '10px' }}>
                        {metrica.icono}
                      </div>
                      <h3 
                        className={
                          typeof metrica.color === 'string' && !metrica.color.startsWith('#')
                            ? `mb-1 text-${metrica.color}`
                            : 'mb-1'
                        }
                        style={{ 
                          color: typeof metrica.color === 'string' && metrica.color.startsWith('#') 
                            ? metrica.color 
                            : undefined 
                        }}
                      >
                        {metricas[metrica.key] || 0}
                      </h3>
                      <p className="text-muted mb-0 small">{metrica.titulo}</p>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>

            {/* ESTADO DE DOCUMENTOS */}
            <Row className="mb-4">
              <Col md={3} className="mb-3">
                <Card className="h-100 border-0 shadow-sm">
                  <Card.Body className="text-center">
                    <div style={{ fontSize: '2rem', marginBottom: '10px' }}>⌛</div>
                    <h4 className="mb-1 text-warning">
                      {metricas.documentosPendientes}
                    </h4>
                    <p className="text-muted mb-0">Pendientes</p>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={3} className="mb-3">
                <Card className="h-100 border-0 shadow-sm">
                  <Card.Body className="text-center">
                    <div style={{ fontSize: '2rem', marginBottom: '10px' }}>✅</div>
                    <h4 className="mb-1 text-success">
                      {metricas.documentosFinalizados}
                    </h4>
                    <p className="text-muted mb-0">Finalizados</p>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={3} className="mb-3">
                <Card className="h-100 border-0 shadow-sm">
                  <Card.Body className="text-center">
                    <div style={{ fontSize: '2rem', marginBottom: '10px' }}>❌</div>
                    <h4 className="mb-1 text-danger">
                      {metricas.documentosRechazados}
                    </h4>
                    <p className="text-muted mb-0">Rechazados</p>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={3} className="mb-3">
                <Card className="h-100 border-0 shadow-sm">
                  <Card.Body className="text-center">
                    <div style={{ fontSize: '2rem', marginBottom: '10px' }}>🚫</div>
                    <h4 className="mb-1 text-secondary">
                      {metricas.documentosAnulados}
                    </h4>
                    <p className="text-muted mb-0">Anulados</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* ÚLTIMOS DOCUMENTOS */}
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white border-0 py-3">
                <h5 className="mb-0">📋 Actividad Reciente</h5>
                <small className="text-muted">Últimos documentos procesados</small>
              </Card.Header>
              <Card.Body className="p-0">
                {ultimosDocumentos.length > 0 ? (
                  <Table responsive className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Tipo</th>
                        <th>Fecha</th>
                        <th>Cédula</th>
                        <th>Paciente</th>
                        <th>Documento</th>
                        <th>Estado</th>
                        <th>Enviado por</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ultimosDocumentos.map((doc) => (
                        <tr key={doc._id}>
                          <td>
                            <span style={{ fontSize: '1.2rem' }}>
                              {obtenerIconoTipo(doc.tipo || 'medico')}
                            </span>
                          </td>
                          <td>
                            <small>{formatearFecha(doc.createdAt)}</small>
                          </td>
                          <td>
                            <code>{doc.cedula}</code>
                          </td>
                          <td>{doc.nombres || '-'}</td>
                          <td>
                            <small>{doc.nombreDocumento}</small>
                          </td>
                          <td>{obtenerBadgeEstado(doc.estado)}</td>
                          <td>
                            <small>{decodificarNombre(doc.enviadoPor || 'No identificado')}</small>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-muted mb-0">📭 No hay documentos recientes</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}

export default DashboardPage;